import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PlannedResource,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { RESOURCE_STATUSES } from 'components/IAP/Resources/constants/resourceConstants';
import { NULL_VALUE } from 'constants/displayConstants';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ICS204_TABLE,
} from './assignmentCardStyles';

type AssignmentCardResourceDialogProps = {
  walaId: string | null;
  onClose: () => void;
  plannedResources: PlannedResource[] | null;
  /**
   * When supplied, the picker hands the chosen set to the caller instead of
   * writing to the server. The ICS-204 edit form uses this so an add lands in
   * its draft buffer and is persisted by "Save as Draft" rather than being
   * clobbered by it.
   */
  onCommit?: (selection: ResourceSelection[]) => void;
};

export type ResourceSelection = {
  id: string;
  name: string;
};

type IncidentResource = {
  id: string;
  name: string;
  kind: string;
  type: string;
  quantity: number;
  availability: string;
  location: string;
};

const DIALOG_SURFACE =
  'dark tw:max-w-[126rem] tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';

const AssignmentCardResourceDialog = ({
  walaId,
  onClose,
  plannedResources,
  onCommit,
}: AssignmentCardResourceDialogProps) => {
  const { data: resources } = useResourcesInIncident();
  const { mutate: update } = useUpdateWorkAssignmentListAttachment();

  const incidentResourceData = useMemo<IncidentResource[]>(() => {
    return (
      resources
        ?.filter((res) => res.current_status !== RESOURCE_STATUSES.CHECKED_OUT)
        .map((res) => ({
          id: res.id,
          name: res.resource_name || NULL_VALUE,
          kind: res.resource_set_kind || NULL_VALUE,
          type: res.resource_set_type || NULL_VALUE,
          quantity: res.resource_set_quantity || 0,
          availability: NULL_VALUE,
          location: res.location_geolocation?.name || 'Unknown',
        })) || []
    );
  }, [resources]);

  const [selected, setSelected] = useState<Set<string>>(
    new Set(
      plannedResources
        ?.filter((resource) => !resource.isDocumentationEntry)
        .map((resource) => resource.id) || []
    )
  );

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSubmit = () => {
    const selectedIds = Array.from(selected);

    if (onCommit) {
      const nameById = new Map(
        incidentResourceData.map((res) => [res.id, res.name])
      );
      onCommit(selectedIds.map((id) => ({ id, name: nameById.get(id) ?? '' })));
      onClose();
      return;
    }

    if (!walaId) return;

    const documentationEntries = (plannedResources || []).filter(
      (resource) => resource.isDocumentationEntry
    );

    const existingResources = (plannedResources || [])
      .filter(
        (resource) =>
          !resource.isDocumentationEntry && selectedIds.includes(resource.id)
      )
      .map(({ id }) => ({ id }) as PlannedResource);

    const newResources = selectedIds
      .filter((id) => !plannedResources?.some((resource) => resource.id === id))
      .map((id) => ({ id }) as PlannedResource);

    update({
      walaId,
      plannedResources: [
        ...documentationEntries,
        ...newResources,
        ...existingResources,
      ],
    });
    onClose();
  };

  const [searchText, setSearchText] = useState('');

  const filteredList = useMemo(() => {
    if (!searchText) return incidentResourceData;
    const lowerSearch = searchText.toLowerCase();
    return incidentResourceData.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerSearch) ||
        item.kind.toLowerCase().includes(lowerSearch) ||
        item.type.toLowerCase().includes(lowerSearch)
    );
  }, [incidentResourceData, searchText]);

  const incidentId = useActualIncidentId();
  const resourcesLink = `/iap/${incidentId}/resources`;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={DIALOG_SURFACE}
        showCloseButton={false}
        data-testid="ics204-attach-resources-modal"
      >
        <DialogHeader>
          <div className="tw:flex tw:w-full tw:items-end tw:justify-between tw:gap-2">
            <DialogTitle>Select Resources to Assign</DialogTitle>
            <Link
              to={resourcesLink}
              target="_blank"
              className="tw:text-xs tw:text-[var(--text-weak)] tw:underline tw:underline-offset-2"
            >
              + Add New Resource
            </Link>
          </div>
        </DialogHeader>
        <Input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search resources by name, kind, or type..."
          className="tw:bg-black tw:text-foreground"
        />
        <div className="tw:max-h-[60vh] tw:overflow-auto">
          <Table className={ICS204_TABLE}>
            <TableHeader>
              <TableRow className="tw:hover:bg-transparent">
                <TableHead className="tw:w-10 tw:text-[var(--text-weak)]" />
                <TableHead className="tw:text-[var(--text-weak)]">
                  Resource Name
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Kind
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Type
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Quantity
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Availability
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Location
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className={ICS204_MUTED}>
                    {incidentResourceData.length === 0
                      ? 'No resources yet. Add a new resource.'
                      : 'No resources fit your search. Add a new resource.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredList.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(row.id)}
                        onCheckedChange={() => toggleSelected(row.id)}
                        aria-label={`Select ${row.name}`}
                      />
                    </TableCell>
                    <TableCell className="tw:text-foreground">
                      {row.name}
                    </TableCell>
                    <TableCell className="tw:text-foreground">
                      {row.kind}
                    </TableCell>
                    <TableCell className="tw:text-foreground">
                      {row.type}
                    </TableCell>
                    <TableCell className="tw:text-foreground">
                      {row.quantity}
                    </TableCell>
                    <TableCell className="tw:text-foreground">
                      {row.availability}
                    </TableCell>
                    <TableCell className="tw:text-foreground">
                      {row.location}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button type="button" onClick={handleSubmit}>
            Add Selected Resources
          </Button>
          <Button
            type="button"
            variant="outline"
            className={ICS204_OUTLINE_BTN}
            onClick={onClose}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentCardResourceDialog;
